/**
 * Terrain generator
 * Creates procedural terrain with vertex coloring
 */

import * as THREE from 'three';

export function createTerrain(): THREE.Mesh {
  const size = 2000;
  const segments = 100;
  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);

  // Apply heightmap and vertex coloring
  const positions = geometry.attributes.position;
  const colors = new Float32Array(positions.count * 3);

  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const z = positions.getY(i); // In PlaneGeometry, Y is actually Z

    // Generate height using simplified noise function
    const height = calculateHeight(x, z);
    positions.setZ(i, height);

    // Color based on height
    const color = getTerrainColor(height);
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.computeVertexNormals();

  // Material with vertex colors
  const material = new THREE.MeshPhongMaterial({
    vertexColors: true,
    flatShading: true,
    side: THREE.DoubleSide,
  });

  const terrain = new THREE.Mesh(geometry, material);
  terrain.rotation.x = -Math.PI / 2; // Rotate to be horizontal
  terrain.receiveShadow = true;

  return terrain;
}

function calculateHeight(x: number, z: number): number {
  // Simple procedural height calculation
  // Using multiple sine waves for varied terrain
  const scale1 = 0.01;
  const scale2 = 0.005;
  const scale3 = 0.002;

  let height = 0;
  height += Math.sin(x * scale1) * 15;
  height += Math.cos(z * scale1) * 15;
  height += Math.sin((x + z) * scale2) * 25;
  height += Math.cos((x - z) * scale3) * 40;

  // Add some randomness
  height += (Math.sin(x * 0.1) * Math.cos(z * 0.1)) * 10;

  return Math.max(height, -50); // Minimum height
}

function getTerrainColor(height: number): { r: number; g: number; b: number } {
  // Desert color palette
  const tan = { r: 0.79, g: 0.65, b: 0.48 }; // #C9A77A
  const brown = { r: 0.69, g: 0.54, b: 0.36 }; // #AF8A5D
  const darkBrown = { r: 0.55, g: 0.44, b: 0.29 }; // Darker brown

  // Interpolate based on height
  if (height < -20) {
    return darkBrown;
  } else if (height < 0) {
    const t = (height + 20) / 20;
    return lerpColor(darkBrown, brown, t);
  } else if (height < 30) {
    const t = height / 30;
    return lerpColor(brown, tan, t);
  } else {
    return tan;
  }
}

function lerpColor(
  c1: { r: number; g: number; b: number },
  c2: { r: number; g: number; b: number },
  t: number
): { r: number; g: number; b: number } {
  return {
    r: c1.r + (c2.r - c1.r) * t,
    g: c1.g + (c2.g - c1.g) * t,
    b: c1.b + (c2.b - c1.b) * t,
  };
}
